<template>
    <div class="page">
        <h1>Help</h1>
        <ul class="tabs fx-hidden--small fx-hidden--medium">
            <li
                class="tabs__item"
                v-for="(category, index) in categories"
                :key="category.label">
                <button
                    class="tabs__button"
                    :class="{ 'tabs__button--active': selectedCategoryIndex === index }"
                    type="button"
                    @click="setSelectedCategoryIndex(index)">
                    <span class="tabs__button-inner" :class="{'tabs__button-inner--last': categories.length - 1 === index}">{{ category.label }}</span>
                </button>
            </li>
        </ul>

        <div class="filter fx-hidden--large fx-hidden--full">
            <fx-select
                v-model="selectedCategoryIndex"
                :options="categories"
                :default-option="null"
                @input="setSelectedCategoryIndex(selectedCategoryIndex)">
                <span class="icon-chevron-lg-down" slot="caret"></span>
            </fx-select>
        </div>

        <div
            class="contact"
            v-if="selectedCategoryIndex === categories.length - 1">
            <div class="contact__row">
                <section class="contact__section">
                    <h3 class="contact__heading">Send us an email</h3>
                    <p>
                        Contact our customer service team now at <a :href="`mailto:${emailAddress}`">{{ emailAddress }}</a>
                        <br>
                        We&rsquo;ll get back to you promptly.
                    </p>
                    <a
                        class="contact__action"
                        :href="`mailto:${emailAddress}`">Send Email</a>
                </section>
                <section class="contact__section">
                    <h3 class="contact__heading">Give us a call</h3>
                    <p>
                        Give us a call and we will be happy to assist you.
                        <br>
                        Mon-Sun 9am - 9pm (EST).
                    </p>
                    <a
                        class="contact__action"
                        v-if="isPhone"
                        :href="`tel:${phoneNumber.replace(/\D/g, '')}`">Get Number</a>
                    <span
                        class="contact__action contact__action--terminal"
                        v-else-if="isShowPhoneNumber">{{ phoneNumber }}</span>
                    <button
                        class="contact__action"
                        type="button"
                        v-else
                        @click="isShowPhoneNumber = true">Get Number</button>
                </section>
            </div>

            <partner-contact />

        </div>

        <ul v-else>
            <li
                class="section"
                v-for="section in categories[selectedCategoryIndex].sections"
                :key="section.heading">
                <collapsible
                    :body-html="section.bodyHtml"
                    :heading="section.heading" />
            </li>
        </ul>
    </div>
</template>

<script>
import collapsible from '../components/Collapsible.vue'
import fxSelect from 'core/components/SelectComponent.vue'
import helpJson from 'helpJson'
import MobileDetect from 'mobile-detect'
import partnerContact from 'partnerContact'
import quebecUserModal from '../mixins/QuebecUserModal'
import trackable from '../mixins/Trackable'

export default {
    components: {
        collapsible,
        partnerContact,
        fxSelect
    },
    beforeCreate () {
        this.htmlPageTitle = `Help at ${this.$config.partnerName}`
    },
    created () {
        this.isPhone = Boolean(new MobileDetect(window.navigator.userAgent).phone())
        this.categories = helpJson.categories.concat({
            label: 'Contact Us',
            sections: []
        })
        this.categories.forEach((category, index) => {
            category.value = index
        })
        this.emailAddress = this.$config.contactEmailOverride || 'care@vividseats.com'
        this.phoneNumber = this.$config.contactPhoneOverride || '1-866-970-3269'

        this.trackPageView(this.$route.fullPath, this.htmlPageTitle)
    },
    data () {
        return {
            isShowPhoneNumber: false,
            selectedCategoryIndex: 0
        }
    },
    head: {
        title () {
            return {
                inner: this.htmlPageTitle
            }
        }
    },
    methods: {
        setSelectedCategoryIndex (index) {
            this.selectedCategoryIndex = index
        }
    },
    mixins: [
        quebecUserModal,
        trackable
    ]
}
</script>

<style lang="postcss" scoped>
@import 'theme';

h1 {
    font-size: 22px;
    font-weight: 500;
    margin: 0 0 30px;
}

.contact {
    & .contact__action {
        background: transparent;
        border: 1px solid var(--c-primary);
        border-radius: 25px;
        color: var(--c-primary);
        cursor: pointer;
        display: inline-block;
        font-size: 14px;
        font-weight: 300;
        margin-top: 5px;
        padding: 10px 20px;
        text-decoration: none;

        &:hover:not(.contact__action--terminal) {
            background: var(--c-primary);
            border: 1px solid var(--c-primary);
            color: #fff;
        }

        &.contact__action--terminal {
            cursor: default;
        }
    }

    & >>> .contact__row {
        display: flex;
        flex-direction: column;
    }

    & >>> .contact__heading {
        font-size: 16px;
        font-weight: 500;
        margin: 24px 0 16px;
    }

    & >>> .contact__section {
        flex: 0 1 auto;
        margin-bottom: 40px;
    }

    @media (--mq-large-min) {
        & >>> .contact__row {
            flex-direction: row;
        }

        & >>> .contact__section {
            flex: 0 1 50%;
        }
    }
}

.page {
    margin: 0 auto;
    max-width: 100%;
    padding: 48px var(--site-padding);
    width: var(--site-container-width);
}

.section {
    border-bottom: 2px solid #efefef;
}

.tabs {
    box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.1);
    border-radius: 5px;
    display: flex;
    flex-direction: row;
    margin-bottom: 20px;
    -ms-overflow-style: none;
    overflow-x: auto;

    & .tabs__button {
        background: white;
        border: none;
        border-left: 1px solid #efefef;
        color: inherit;
        cursor: pointer;
        font-size: 12px;
        height: 100%;
        padding: 10px 5px;
        width: 100%;

        &.tabs__button--active {
            color: #1c74a5;
        }
    }

    & .tabs__item {
        flex: 1 1 auto;
        text-align: center;
        white-space: pre-wrap;
        width: 100%;
        &:first-child {
            & .tabs__button-inner {
                border-left: none;
            }
        }
    }

    @media (--mq-medium-min) {
        & .tabs__button {
            border-left: none;
            font-size: 16px;
            height: 100%;
            padding: 15px 0;
        }

        & .tabs__button-inner {
            align-items: center;
            border-left: 1px solid #efefef;
            display: flex;
            height: 100%;
            justify-content: center;
            padding: 0 15px;

            &.tabs__button-inner--last {
                white-space: nowrap;
            }
        }

        & .tabs__item {
            &:first-child {
                & .tabs__button-inner {
                    border-left: none;
                }
            }
        }
    }
}

.filter {
    padding-left: 2px;
    padding-top: 12px;
    padding-bottom: 12px;
    position: relative;
    align-self: flex-end;

    & .fx-date-range {
        display: inline-block;
    }

    & >>> .fx-selection {
        color: #1c74a5;
        font-size: 16px;
        text-decoration: underline;
        margin-right: 16px !important;
    }

    & >>> .icon-cancel {
        color: #1c74a5;
        font-size: 14px;
        position: absolute;
        right: 0;
        top: 8px;
    }

    & >>> .fx-select-input {
        border-bottom: 0;
    }

    & >>> .icon-chevron-lg-down {
        color: #1c74a5;
        font-size: 14px;
        position: absolute;
        right: 0;
        top: 7px;
    }

    & >>> .fx-select-dropdown {
        & li {
            padding: 10px 16px;

            &.selected,
            &:active,
            &:focus,
            &:hover {
                background: linear-gradient(-90deg, rgba(60, 172, 209, 0.00) 0%, rgba(28, 116, 165, 0.13) 100%);
                border-left: 4px solid #1c74a5;
                color: #1c74a5;
                padding-left: 12px;
            }
        }
    }
}
</style>
